import React, { useMemo } from "react";
import "../../../../common/scss/table.scss";
import "./KpiRawDataTable.scss";
import defaultContent from "../../../../content/downlineProfile";
import NoDataValue from "../../../../common/components/NoDataValue/NoDataValue";
import { useTranslation } from "react-i18next";
import { NormalizedSeriesData, type KpiSeries } from "../types";
import { normalizeSeriesData } from "../utils";
import LeafRectangleLoader from "../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";

interface KpiRawDataTableHeadProps {
  selectedKpis?: string[];
  selectedPerformanceYear?: number;
}

interface KpiRawDataTableProps {
  series?: KpiSeries[] | null;
  selectedKpis?: string[];
  selectedPerformanceYear?: number;
  locale?: string;
  endPeriod?: number;
  isLoading?: boolean;
}

const KpiRawDataTableHead: React.FC<KpiRawDataTableHeadProps> = ({ selectedKpis, selectedPerformanceYear }) => {
  const { t } = useTranslation(["downlineProfile"]);
  const pyYear = (selectedPerformanceYear ?? 0) % 100;
  return (
    <>
      <thead>
        <tr>
          <th className="KpiRawDataTable__month KpiRawDataTable__month-header">
            {t(`keyInformation.awardLabels.basePYDate`, defaultContent.keyInformation.awardLabels.basePYDate, {
              date: pyYear,
            })}
          </th>
          {selectedKpis?.map((name, i) => {
            return (
              <th key={i} className="KpiRawDataTable__kpi-header">
                {t(
                  `performanceMetrics.${name}`,
                  defaultContent.performanceMetrics[name as keyof typeof defaultContent.performanceMetrics],
                )}
              </th>
            );
          })}
        </tr>
      </thead>
    </>
  );
};

interface KpiRawDataTableRowProps {
  period?: NormalizedSeriesData;
}

const KpiRawDataTableRow: React.FC<KpiRawDataTableRowProps> = ({ period }) => {
  return (
    <tr key={period?.month}>
      <td className="KpiRawDataTable__month">{period?.monthLabel}</td>
      {period?.values.map(({ kpi, point }) => (
        <td key={kpi}>{point?.value.formatted ?? <NoDataValue label={"No Data"} />}</td>
      ))}
    </tr>
  );
};

const SKELETON_ROW_COUNT = 9;
const SKELETON_COL_COUNT = 5;

const KpiRawDataTable: React.FC<KpiRawDataTableProps> = ({
  selectedKpis,
  series,
  selectedPerformanceYear,
  locale,
  endPeriod,
  isLoading,
}) => {
  const normalizeSeries: NormalizedSeriesData[] = useMemo(() => {
    return normalizeSeriesData(series, selectedKpis, selectedPerformanceYear, locale, endPeriod);
  }, [series, selectedKpis, selectedPerformanceYear, locale, endPeriod]);

  const colCount = selectedKpis?.length ?? SKELETON_COL_COUNT;

  return (
    <div className="KpiRawDataTable__wrapper">
      <table
        className="KpiRawDataTable Table Table--rounded Table--alignCenter TableBody--rowBorder"
        data-testid="kpi-raw-data-table"
        aria-label="KPI raw data table"
      >
        <colgroup>
          <col className="KpiRawDataTable__col-month" />
          {Array.from({ length: colCount }).map((_, i) => (
            <col key={i} className="KpiRawDataTable__col-kpi" />
          ))}
        </colgroup>
        {isLoading ? (
          <>
            <thead>
              <tr>
                <th className="KpiRawDataTable__month KpiRawDataTable__month-header">
                  <LeafRectangleLoader isLoading width="4rem" height="1rem" />
                </th>
                {Array.from({ length: colCount }).map((_, i) => (
                  <th key={i} className="KpiRawDataTable__kpi-header">
                    <LeafRectangleLoader isLoading width="5rem" height="1rem" />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: SKELETON_ROW_COUNT }).map((_, r) => (
                <tr key={r}>
                  <td className="KpiRawDataTable__month">
                    <LeafRectangleLoader isLoading width="4rem" height="1rem" />
                  </td>
                  {Array.from({ length: colCount }).map((_, c) => (
                    <td key={c}>
                      <LeafRectangleLoader isLoading width="4rem" height="1rem" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </>
        ) : (
          <>
            <KpiRawDataTableHead selectedKpis={selectedKpis} selectedPerformanceYear={selectedPerformanceYear} />
            <tbody>
              {normalizeSeries.map((period, i) => {
                return <KpiRawDataTableRow key={i} period={period} />;
              })}
            </tbody>
          </>
        )}
      </table>
    </div>
  );
};
export default KpiRawDataTable;
